import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAppSelector } from '../store/hooks';
import apiClient from '../api/client';
import type { SimulationResult } from '../types/defi';

type PriceChanges = Record<string, number>;

const PortfolioSimulationPage: React.FC = () => {
  const userId = useAppSelector((s) => s.auth.user?.id);
  const [priceChangesText, setPriceChangesText] = useState('{"BTC": -20, "ETH": -25}');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SimulationResult | null>(null);

  const runSimulation = async () => {
    if (!userId || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);

    let parsedPriceChanges: PriceChanges;
    try {
      parsedPriceChanges = JSON.parse(priceChangesText) as PriceChanges;
    } catch {
      setLoading(false);
      setError('Price changes must be valid JSON (example: {"BTC": -20})');
      return;
    }

    try {
      const res = await apiClient.post<SimulationResult>(
        `/analytics/simulate/${userId}`,
        { priceChanges: parsedPriceChanges }
      );
      setResult(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to run simulation');
    } finally {
      setLoading(false);
    }
  };

  if (!userId) {
    return <div className="text-text-muted">Please log in to run simulations.</div>;
  }

  const safePositions = result
    ? result.assets.filter((a) => !a.liquidationRiskFlag).length
    : 0;
  const liquidatedPositions = result ? result.liquidationRiskAssets.length : 0;

  const chartData = result
    ? [
        { name: 'Simulated Value (USD)', value: Number(result.simulatedTotalValueUsd || 0) },
        { name: 'Baseline Value (USD)', value: Number(result.currentTotalValueUsd || 0) },
        { name: 'Positions at Risk', value: liquidatedPositions },
        { name: 'Safe Positions', value: safePositions },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Portfolio Simulation</h1>
        <p className="text-sm text-text-secondary mt-1">Run what-if stress scenarios on current positions.</p>
      </div>

      <div className="rounded-xl border border-border bg-surface p-6 space-y-3">
        <label className="text-sm font-medium text-text-primary">Price change JSON (percentages, e.g. -20 = -20%)</label>
        <textarea
          value={priceChangesText}
          onChange={(e) => setPriceChangesText(e.target.value)}
          rows={5}
          className="w-full rounded-lg bg-app border border-border px-3 py-2 font-mono text-sm text-text-primary"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={runSimulation}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-60 text-white"
          >
            {loading ? 'Running…' : 'Run Simulation'}
          </button>
          {error && <span className="text-sm text-red-300">{error}</span>}
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-border bg-surface p-6 text-text-muted">Evaluating scenario…</div>
      ) : result ? (
        <div className="rounded-xl border border-border bg-surface p-6 space-y-2">
          <h2 className="text-lg font-semibold text-text-primary">Simulation Result</h2>
          <p className="text-sm text-text-primary">
            Baseline Value: ${Number(result.currentTotalValueUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-sm text-text-primary">
            Simulated Value: ${Number(result.simulatedTotalValueUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-sm text-text-primary">
            Change: {Number(result.totalChangePercent || 0).toFixed(2)}%
            ({Number(result.totalChangeDollars || 0) >= 0 ? '+' : ''}${Number(result.totalChangeDollars || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
          </p>
          <p className="text-sm text-text-primary">
            Positions at Liquidation Risk: {liquidatedPositions}
            {result.liquidationRiskAssets.length > 0 && (
              <span className="text-red-300 ml-1">({result.liquidationRiskAssets.join(', ')})</span>
            )}
          </p>
          <p className="text-sm text-text-primary">
            Safe Positions: {safePositions}
          </p>
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="value" fill="#3b82f6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-surface p-6 text-text-muted">
          No simulation has been run yet.
        </div>
      )}
    </div>
  );
};

export default PortfolioSimulationPage;
